import { LitElement, css, html } from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { t } from '@nova/utils'

class NovaQuantityInput extends LitElement {
  static properties = {
    key: {},
    label: {},
    initial: {
      type: Number,
    },
    value: {
      state: true,
    },
    min: {
      type: Number,
    },
    max: {
      type: String,
      attribute: 'max',
    },
    index: {
      type: Number
    }
  }

  constructor() {
    super()

    this.broadcast = this.broadcast.bind(this)
    this.id = Math.random() * 100000
    this.max = ''
    this.min = 1
    this.index = 0
  }

  connectedCallback() {
    super.connectedCallback()
    this.value = this.initial

    if (!this.classList.contains('js-product-quantity-selector')) return

    this.updateContent = this.updateContent.bind(this)
    this._listensForProductQuantity = true
    document.addEventListener(
      'nova:product:render-quantity-input',
      this.updateContent,
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (!this._listensForProductQuantity) return

    document.removeEventListener(
      'nova:product:render-quantity-input',
      this.updateContent,
    )
  }

  updateContent(event) {
    const newMax = event.detail.html?.getAttribute('max')
    if (!newMax || newMax === null) return this.setAttribute('max', '')

    this.setAttribute('max', newMax)
    this.max = newMax
    if (this.value > Number(newMax)) {
      this.value = Number(newMax)
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initial')) {
      this.value = this.initial
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('key')) {
      this.value = this.initial
    }
  }

  get isMax() {
    return this.max !== '' && this.value >= Number(this.max)
  }

  get isMin() {
    return this.value <= this.min
  }

  broadcast() {
    const $input = this.renderRoot.querySelector('slot[name="input"]')

    if ($input) {
      $input.value = this.value
    }

    this.dispatchEvent(
      new CustomEvent('changeQuantityStarted', {
        bubbles: true,
        composed: true,
        detail: {
          key: this.key,
          value: this.value,
          index: this.index,
        },
      }),
    )
  }

  increment() {
    if (this.isMax) return
    this.value += 1
    this.broadcast()
  }

  decrement() {
    if (this.isMin) return
    this.value -= 1
    this.broadcast()
  }

  override(e) {
    let value = Number(e.target.value)

    if (isNaN(value)) {
      value = this.min
    }

    if (this.max !== '' && value > Number(this.max)) {
      value = Number(this.max)
    }

    if (value < this.min) {
      value = this.min
    }

    this.value = value
    this.broadcast()
  }


  render() {
    return html`
      <div class="wrapper" part="wrapper">
        <button
          @click=${this.decrement}
          name="decrease"
          class="decrease"
          part="decrease"
          ?disabled=${this.isMin}
          aria-label=${t('accessibility.decrease_quantity', 'Decrease quantity')}
        >
          ${paths.minus}
        </button>

        <label class="sr-only" for="Quantity-${this.id}">
          ${this.label || t('accessibility.quantity', t('products.product.quantity.label', 'Quantity'))}
        </label>

        <slot name="input"></slot>

        <input
          id="Quantity-${this.id}"
          class="input"
          type="number"
          name="updates[]"
          .value=${this.value}
          @change=${this.override}
          min=${this.min}
          max=${this.max}
        />

        <button
          @click=${this.increment}
          name="increase"
          class="increase"
          part="increase"
          ?disabled=${this.isMax}
          aria-label=${t('accessibility.increase_quantity', 'Increase quantity')}
        >
          ${paths.plus}
        </button>
      </div>
    `
  }

  static styles = [
    styles.a11y,
    styles.variables,
    css`
      .wrapper {
        border: 1px solid var(--nova-brand-neutral);
        border-radius: var(--nova-border-radius-s);
        box-sizing: border-box;
        color: #000;
        position: relative;
        display: flex;
        height: var(--nova-forms-input-height);
        width: 100%;
      }

      .input {
        color: currentColor;
        font: #000;
        text-align: center;
        border: 0;
        width: 100%;
        flex-grow: 1;
        -webkit-appearance: none;
        appearance: none;
        -moz-appearance: textfield;
      }

      button {
        height: var(--nova-button-height-s);
        width: calc(var(--nova-space-sm) + var(--nova-space-xs));
        flex-shrink: 0;
        border: 0;
        background-color: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000;
      }

      .decrease {
        padding: 0 0 0 var(--nova-space-sm);
      }

      .increase {
        padding: 0 var(--nova-space-sm) 0 0;
      }

      button:hover {
        color: #000;
      }

      .input:-webkit-autofill,
      .input:-webkit-autofill:hover,
      .input:-webkit-autofill:active {
        box-shadow: 0 0 0 10rem var(--color-background) inset !important;
        -webkit-box-shadow: 0 0 0 10rem var(--color-background) inset !important;
      }

      .input::-webkit-outer-spin-button,
      .input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      button[disabled] {
        pointer-events: none;
        opacity: 0.3;
        cursor: not-allowed;
      }
    `,
  ]
}

customElements.define('nova-quantity-input', NovaQuantityInput)
